import React from 'react';
import { Box } from '@mui/material';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { useLoggedInUserName } from 'src/hooks/useLoggedInUserName';
import { PEDIGREE } from 'src/components/Constants';
import {
  config,
  GENDER,
  canCarryPregnancy,
  showsReproductiveSection
} from '../../../pedigree-tool/pedigree/config';
import {
  getAllowedRelativeActions,
  RELATIVE_ACTIONS
} from '../../../pedigree-tool/pedigree/utils';
import { ADD_RELATIVE_KEYS } from '../../../pedigree-tool/keyboardShortcuts';
import {
  resolveSelf,
  resolveOwnerPid,
  hasParents as hasParentsOf,
  hasGrandparents as hasGrandparentsOf
} from '../../../pedigree-tool/pedigree/relativePredicates';
import {
  PregnancyModal,
  spouseOptionsFor,
  usePregnancyCrud
} from '../../../pedigree-tool/canvas-tools/options/pregnancyEditor';
import GameteDonorModal from '../../../pedigree-tool/canvas-tools/options/GameteDonorModal';
import {
  addParents,
  addGrandparents,
  addSpouse,
  addBrother,
  addSister,
  addSon,
  addDaughter,
  addUnknown
} from '../../../pedigree-tool/canvas-tools/options/addRelativeActions';
import RelativeGroup from './RelativeGroup';
import RelativeTile from './RelativeTile';
import RecordedChip from './RecordedChip';
import ReproLauncher from './ReproLauncher';
import ChildScope from './ChildScope';
import AddConfirmation from './AddConfirmation';
import { PersonSymbol, SpouseSymbol, ReproSymbol } from './TileSymbol';
import { undoAddBatch } from './addBatchUndo';
import { usePruneOrderForStructuralEdit } from '../../../pedigree-tool/structuralEditContext';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// The adds that can move an EXISTING person to another row — see commitAdd.
const STRUCTURAL_ADD_KINDS = new Set(['parents', 'grandparents']);

/**
 * @typedef  {Object} AddBatch
 * @property {string}   kind      'brother'|'sister'|'son'|'daughter'|'spouse'
 *                                |'parents'|'grandparents'|'unknownPerson'
 * @property {?number}  spouseId  Set only for child adds. Scopes label and undo.
 *                                NOTE: no 'fetus' and no 'donor' — the launchers
 *                                never enter a batch.
 * @property {number}   count
 * @property {number[]} createdIds  Pids of every node created, in creation order.
 */

const PLURAL = {
  brother: ['brother', 'brothers'],
  sister: ['sister', 'sisters'],
  son: ['son', 'sons'],
  daughter: ['daughter', 'daughters'],
  spouse: ['spouse', 'spouses'],
  parents: ['parents', 'parents'],
  grandparents: ['grandparents', 'grandparents'],
  unknownPerson: ['unknown person', 'unknown people']
};

const CONFIRMATION_MS = 4000;

/**
 * Add Relative — the pedigree sidebar's panel for putting nodes on a chart.
 *
 * Four groups by generation. The only file here that touches memberRecords: the
 * tiles emit `onAdd(count)` and know nothing about spouses or the pedigree.
 *
 * ONE ADD PER SAVE ROUND-TRIP, by design (decision D1). Every add sets
 * pedigreeState.save = IN_PROGRESS, which arrives back as `isDisabled` and greys
 * the panel until the POST settles. That gate is what fixed TGCLOUD-1490 (rapid
 * adds landing on the wrong generation because the save response reverted
 * mid-flight edits), so the count stepper — not a run of clicks — is how you add
 * several at once.
 */
const AddRelativePanel = ({
  node,
  user,
  memberRecords,
  setMemberRecords,
  setPedigreeState,
  isDisabled = false
}) => {
  const t = useAutoTranslation();

  // Tolerate a missing provider: the walkthrough anchor-drift guard renders this
  // panel in isolation on purpose (no backend, no app contexts), and a bare
  // destructure of a null context throws before a single anchor reaches the DOM.
  const { setSnack } = React.useContext(SnackContext) || {};
  const pruneOrderForStructuralEdit = usePruneOrderForStructuralEdit();
  const { content } = React.useContext(AppContext) || {};
  const familyDiagnoses = content?.pedigreeSelectedUsers?.diagnoses || [];
  const recordedBy = useLoggedInUserName();

  const ownerPid = resolveOwnerPid(node);
  const self = React.useMemo(
    () => resolveSelf(node, memberRecords),
    [memberRecords, node]
  );

  const allowedActions = getAllowedRelativeActions(node);
  const allow = React.useMemo(
    () => ({
      parents: allowedActions.has(RELATIVE_ACTIONS.parents),
      grandparents: allowedActions.has(RELATIVE_ACTIONS.grandparents),
      spouse: allowedActions.has(RELATIVE_ACTIONS.spouse),
      brother: allowedActions.has(RELATIVE_ACTIONS.brother),
      sister: allowedActions.has(RELATIVE_ACTIONS.sister),
      son: allowedActions.has(RELATIVE_ACTIONS.son),
      daughter: allowedActions.has(RELATIVE_ACTIONS.daughter),
      unknown: allowedActions.has(RELATIVE_ACTIONS.unknown)
    }),
    [allowedActions]
  );

  // ---------------------------------------------------------------- predicates
  // Shared with the context menu — see pedigree/relativePredicates.js.
  const hasParents = hasParentsOf(self);
  const hasGrandparents = React.useMemo(
    () => hasGrandparentsOf(self, memberRecords),
    [memberRecords, self]
  );

  // ------------------------------------------------------------- spouse scope
  const spouseOptions = React.useMemo(
    () => spouseOptionsFor(self, memberRecords),
    [self, memberRecords]
  );
  const [spousePid, setSpousePid] = React.useState(null);
  // Default to the MOST RECENTLY added spouse (last of spouseIds), which is what
  // the old panel did and what addChildrenToRecords falls back to.
  const effectiveSpousePid =
    spousePid != null && spouseOptions.some((o) => o.pid === spousePid)
      ? spousePid
      : spouseOptions.length
      ? spouseOptions[spouseOptions.length - 1].pid
      : null;
  const spouseLabelOf = (pid) =>
    spouseOptions.find((o) => o.pid === pid)?.label || null;
  // The selected spouse, a value in the child tiles' sentences ("Add son with
  // …"), which ChildScope words.
  const scopeSpouse = effectiveSpousePid
    ? spouseLabelOf(effectiveSpousePid)
    : null;

  // ------------------------------------------------------------------- batch
  const [batch, setBatch] = React.useState(null);
  const pendingRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const [undoReason, setUndoReason] = React.useState('');

  // Everything transient is keyed to the selected person: a pinned count leaking
  // to the next relative is TGCLOUD-742 ("inadvertently add 3 sisters to next
  // relative"), and a stale confirmation would offer to undo an add on someone
  // else. Remounting the tiles via `key` resets their pending counts too.
  React.useEffect(() => {
    setSpousePid(null);
    setBatch(null);
    setUndoReason('');
    pendingRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
  }, [ownerPid]);

  React.useEffect(() => () => clearTimeout(timerRef.current), []);

  // The created objects are collected INSIDE the setMemberRecords updater, so
  // they only carry their pids once that updater has run. memberRecords changing
  // is the signal that it has.
  React.useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || !pending.created.length) return;
    pendingRef.current = null;
    const createdIds = pending.created.map((p) => p.pid);
    // The words are put together where the confirmation is shown (batchMessage),
    // in the user's language; the batch holds only what they describe.
    setBatch({
      kind: pending.kind,
      spouseId: pending.spouseId,
      count: pending.count,
      createdIds,
      spouseLabel:
        pending.spouseId != null ? spouseLabelOf(pending.spouseId) : null
    });
    setUndoReason('');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setBatch(null), CONFIRMATION_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberRecords]);

  const markSaving = () =>
    setPedigreeState?.((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

  /**
   * Run one add action, remember what it created, and mark the pedigree saving.
   * `run(prev, opts)` must return the next member array and pass `opts` to the
   * action so `onCreate` fires.
   */
  const commitAdd = (kind, count, spouseId, run) => {
    if (isDisabled) return;
    const created = [];
    pendingRef.current = { kind, count, spouseId, created };
    setMemberRecords((prev) => run(prev, { onCreate: (p) => created.push(p) }));
    // New parents or grandparents re-top a person who is not connected to the
    // proband (an FHQ "Other relative", a native "Unknown person"), moving them
    // down one or two rows, so their stored rank goes in the SAME update
    // (TGCLOUD-1786, structuralEditContext.js). The other adds only place new
    // people, and pruning for an add-child would forget a first-time parent's
    // rank for nothing. Running the action again on the records it was computed
    // from gives the same family: only the uids of the new people differ, and
    // they carry no rank.
    if (STRUCTURAL_ADD_KINDS.has(kind)) {
      pruneOrderForStructuralEdit(
        memberRecords || [],
        run(memberRecords || [])
      );
    }
    markSaving();
    // Signal for the Clinician Copilot guided walkthrough — read-only, no payload
    // beyond the type, nothing person-identifying leaves this module. Any add
    // advances a walkthrough step whose advanceOn is 'relative-added'. This is
    // tour plumbing, NOT analytics: it predates this panel and must not be
    // removed under the no-tracking rule.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('va:pedigree', { detail: { type: 'relative-added' } })
      );
    }
  };

  // "Added 3 brothers", "Added 1 son with Ann".
  const batchMessage = (b) => {
    const [one, many] = PLURAL[b.kind];
    const vars = { count: b.count, relatives: t(b.count === 1 ? one : many) };
    return b.spouseLabel
      ? t('Added {{count}} {{relatives}} with {{spouse}}', {
          ...vars,
          spouse: b.spouseLabel
        })
      : t('Added {{count}} {{relatives}}', vars);
  };

  const handleUndo = async () => {
    if (!batch) return;
    const result = await undoAddBatch({
      batch,
      members: memberRecords,
      user,
      setMemberRecords,
      isSaving: isDisabled,
      t
    });
    if (result.ok) {
      setBatch(null);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    setUndoReason(result.reason);
    setSnack?.({ status: true, msg: result.reason, severity: 'warning' });
  };

  // ------------------------------------------------------- reproductive hosts
  const reproOn = config.ENABLE_REPRO_NODES;
  const {
    busy: reproBusy,
    submitPregnancy,
    convertFetusToPerson,
    createDonorPerson
  } = usePregnancyCrud({
    memberRecords,
    setMemberRecords,
    setPedigreeState,
    setSnack,
    user
  });
  const [pregnancyOpen, setPregnancyOpen] = React.useState(false);
  // Holds the DIRECTION being recorded ('received' | 'donated'), or null.
  const [donorMode, setDonorMode] = React.useState(null);
  React.useEffect(() => {
    setPregnancyOpen(false);
    setDonorMode(null);
  }, [ownerPid]);

  // D7 — the SAME three-branch gate the context menu applies, not a fourth
  // definition of who can be pregnant:
  //   assigned male with no pregnancy records → not offered at all (Kathryn's
  //     TGCLOUD-1617 item 6 ruling: it is noise on a record that can never carry)
  //   sex not recorded → offered but DISABLED, carrying the tooltip that is the
  //     only prompt in the app telling a clinician how to fix such a record
  //   female (incl. a trans man, via genderMappings) → offered
  const fetusLauncher =
    reproOn && showsReproductiveSection(self)
      ? {
          disabled: !canCarryPregnancy(self) || reproBusy,
          reason: canCarryPregnancy(self)
            ? ''
            : t(
                'Set Sex Assigned at Birth to Female before adding a pregnancy'
              ),
          onOpen: () => setPregnancyOpen(true)
        }
      : null;

  // The person as held in memberRecords — the source of truth for _id and
  // gameteDonors; `node` is a laid-out copy.
  const targetPerson = (memberRecords || []).find((m) => m.pid === ownerPid);

  // Donor links are RENDER TOPOLOGY, so they persist with the pedigree config on
  // the next save — the same route bioFatherId/bioMotherId takes. No pregnancy
  // record is written, deliberately. The link ALWAYS lands on the RECIPIENT,
  // whichever direction the modal was opened in, which is what lets one stored
  // shape serve both entry points.
  const handleAddDonorLink = ({ recipientPid, role, donorRef }) => {
    if (recipientPid == null) return;
    setMemberRecords?.((prev) =>
      (prev || []).map((m) =>
        m.pid === recipientPid
          ? {
              ...m,
              gameteDonors: [
                ...(Array.isArray(m.gameteDonors) ? m.gameteDonors : []),
                { role, ...(donorRef ? { memberId: String(donorRef) } : {}) }
              ]
            }
          : m
      )
    );
    markSaving();
    setDonorMode(null);
  };

  const handleRemoveDonorLink = ({ recipientPid, index }) => {
    if (recipientPid == null) return;
    setMemberRecords?.((prev) =>
      (prev || []).map((m) => {
        if (m.pid !== recipientPid) return m;
        const next = (
          Array.isArray(m.gameteDonors) ? m.gameteDonors : []
        ).filter((_, i) => i !== index);
        // Back to `undefined` when empty, matching the schema default.
        return { ...m, gameteDonors: next.length ? next : undefined };
      })
    );
    markSaving();
  };

  const showDonorRows = reproOn && !!targetPerson;

  // --------------------------------------------------------------------- view
  return (
    <Box>
      {(allow.parents || allow.grandparents) && (
        <RelativeGroup title={t('Earlier generations')} columns={2}>
          {allow.parents &&
            (hasParents ? (
              <RecordedChip
                label={t('Parents recorded')}
                vaAnchor="pedigree.add-relative.parents"
              />
            ) : (
              <RelativeTile
                vaAnchor="pedigree.add-relative.parents"
                symbol={<SpouseSymbol />}
                label={t('Parents')}
                accessibleName={t('Add parents')}
                shortcut={ADD_RELATIVE_KEYS.parents}
                showStepper={false}
                disabled={isDisabled}
                onAdd={() =>
                  commitAdd('parents', 2, null, (prev, opts) =>
                    addParents(prev, node, user, opts)
                  )
                }
              />
            ))}
          {allow.grandparents &&
            (hasGrandparents ? (
              <RecordedChip
                label={t('Grandparents recorded')}
                vaAnchor="pedigree.add-relative.grandparents"
              />
            ) : (
              <RelativeTile
                vaAnchor="pedigree.add-relative.grandparents"
                symbol={<SpouseSymbol />}
                label={t('Grandparents')}
                accessibleName={t('Add grandparents')}
                shortcut={ADD_RELATIVE_KEYS.grandparents}
                showStepper={false}
                disabled={isDisabled}
                onAdd={() =>
                  commitAdd('grandparents', 4, null, (prev, opts) =>
                    addGrandparents(prev, node, user, opts)
                  )
                }
              />
            ))}
        </RelativeGroup>
      )}

      {(allow.spouse || allow.brother || allow.sister) && (
        <RelativeGroup title={t('Current generation')} columns={3}>
          {/* Spouse is a FIRST-CLASS TILE and the only route to adding one. The
              legacy panel had it as a top-level button and that discoverability
              has to survive; an earlier draft moved it inside the Children
              container, where a reviewer went looking for it and could not find
              it. */}
          {allow.spouse && (
            <RelativeTile
              vaAnchor="pedigree.add-relative.spouse"
              symbol={<SpouseSymbol />}
              label={t('Spouse')}
              accessibleName={t('Add spouse')}
              shortcut={ADD_RELATIVE_KEYS.spouse}
              showStepper={false}
              disabled={isDisabled}
              onAdd={() =>
                commitAdd('spouse', 1, null, (prev, opts) =>
                  addSpouse(prev, node, user, opts)
                )
              }
            />
          )}
          {/* A sibling hangs off the shared parents, so without them there is
              nothing to attach to. The old panel disabled these two silently,
              with no tooltip — an unavailable control has to say why. */}
          {allow.brother && (
            <RelativeTile
              key={`brother-${ownerPid}`}
              vaAnchor="pedigree.add-relative.brother"
              symbol={<PersonSymbol gender="male" />}
              label={t('Brother')}
              accessibleName={t('Add brother')}
              shortcut={ADD_RELATIVE_KEYS.brother}
              disabled={isDisabled || !hasParents}
              disabledReason={hasParents ? '' : t('Add parents first')}
              onAdd={(count) =>
                commitAdd('brother', count, null, (prev, opts) =>
                  addBrother(prev, node, user, count, opts)
                )
              }
            />
          )}
          {allow.sister && (
            <RelativeTile
              key={`sister-${ownerPid}`}
              vaAnchor="pedigree.add-relative.sister"
              symbol={<PersonSymbol gender="female" />}
              label={t('Sister')}
              accessibleName={t('Add sister')}
              shortcut={ADD_RELATIVE_KEYS.sister}
              disabled={isDisabled || !hasParents}
              disabledReason={hasParents ? '' : t('Add parents first')}
              onAdd={(count) =>
                commitAdd('sister', count, null, (prev, opts) =>
                  addSister(prev, node, user, count, opts)
                )
              }
            />
          )}
        </RelativeGroup>
      )}

      {(allow.son || allow.daughter) && (
        <ChildScope
          key={`children-${ownerPid}`}
          spouseOptions={spouseOptions}
          selectedSpousePid={effectiveSpousePid}
          onSelectSpouse={setSpousePid}
          disabled={isDisabled}
          allow={{ son: allow.son, daughter: allow.daughter }}
          scopeSpouse={scopeSpouse}
          fetus={fetusLauncher}
          onAddChild={(gender, count) => {
            const spouseId = effectiveSpousePid;
            const kind = gender === GENDER.male ? 'son' : 'daughter';
            const fn = gender === GENDER.male ? addSon : addDaughter;
            commitAdd(kind, count, spouseId, (prev, opts) =>
              fn(prev, node, user, count, { ...opts, coParentPid: spouseId })
            );
          }}
        />
      )}

      {(showDonorRows || allow.unknown) && (
        <RelativeGroup title={t('Other')} columns={1}>
          {/* Gamete donation has TWO directions and both are here, adjacent. They
              write the same edge from opposite ends; omitting either makes the
              panel asymmetric rather than smaller, and leaves a clinician
              standing on the donor's node with no way to record the donation.
              Neither is sex-gated on the selected person — a donor-conceived
              person can be any sex (TGCLOUD-1613), and both sexes can donate. The
              gamete type is where sex matters, and GameteDonorModal warns about a
              mismatch rather than refusing it. */}
          {showDonorRows && (
            <ReproLauncher
              symbol={<ReproSymbol letter="D" />}
              label={t('Conceived using a donor…')}
              accessibleName={t(
                'Record a gamete donor for this person, opens the donor form'
              )}
              disabled={isDisabled || reproBusy}
              onOpen={() => setDonorMode('received')}
            />
          )}
          {showDonorRows && (
            <ReproLauncher
              symbol={<ReproSymbol letter="D" />}
              label={t('Is a gamete donor…')}
              accessibleName={t(
                'Record this person as a gamete donor, opens the donor form'
              )}
              disabled={isDisabled || reproBusy}
              onOpen={() => setDonorMode('donated')}
            />
          )}
          {/* The unconnected person: unknown relationship AND unknown sex,
              floating for the drawer to connect and sex later. NOT a sex-unknown
              child — that misreading is why the bare word "Unknown" is gone — and
              that is also why it has never carried a count. Unlike the launchers
              it does take part in the confirmation and undo. */}
          {allow.unknown && (
            <RelativeTile
              layout="row"
              symbol={<PersonSymbol gender="unknown" />}
              label={t('Unknown person')}
              accessibleName={t(
                'Add an unknown person, unconnected to this family'
              )}
              shortcut={ADD_RELATIVE_KEYS.unknown}
              showStepper={false}
              disabled={isDisabled}
              onAdd={() =>
                commitAdd('unknownPerson', 1, null, (prev, opts) =>
                  addUnknown(prev, node, user, opts)
                )
              }
            />
          )}
        </RelativeGroup>
      )}

      {batch && (
        <AddConfirmation
          message={batchMessage(batch)}
          onUndo={handleUndo}
          undoDisabledReason={undoReason}
        />
      )}

      {pregnancyOpen && (
        <PregnancyModal
          record={null}
          spouseOptions={spouseOptions}
          // TGCLOUD-1617 item 3 — a new record defaults "conceived with" to
          // whichever spouse the child scope has selected, so the launcher adds a
          // not-yet-born child to the same union the tiles above it would.
          initialSpouseRef={
            spouseOptions.find((o) => o.pid === effectiveSpousePid)?.ref ?? null
          }
          familyDiagnoses={familyDiagnoses}
          recordedBy={recordedBy}
          carrier={self}
          memberRecords={memberRecords}
          onSubmit={(formValues) => submitPregnancy(self, null, formValues)}
          onConvert={(rec, gender, fetusIndex) =>
            convertFetusToPerson(self, rec, gender, fetusIndex)
          }
          onCreateDonorPerson={createDonorPerson}
          onClose={() => setPregnancyOpen(false)}
        />
      )}

      {donorMode && targetPerson && (
        <GameteDonorModal
          person={targetPerson}
          memberRecords={memberRecords}
          mode={donorMode}
          onAddLink={handleAddDonorLink}
          onRemoveLink={handleRemoveDonorLink}
          onClose={() => setDonorMode(null)}
          onCreateDonorPerson={createDonorPerson}
          busy={reproBusy}
        />
      )}
    </Box>
  );
};

export default AddRelativePanel;
